
import React, { InputHTMLAttributes, SelectHTMLAttributes, ButtonHTMLAttributes, TextareaHTMLAttributes, forwardRef } from 'react';
import { Loader2 } from 'lucide-react';

// --- WRAPPERS ---

interface FormContainerProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * En flexibel behållare för formulärfält.
 */
export const FormContainer: React.FC<FormContainerProps> = ({ children, className = '' }) => {
  return (
    <div className={`w-full flex flex-col space-y-4 ${className}`}>
      {children}
    </div>
  );
};

// --- TYPOGRAFI ---

interface LabelProps {
  children: React.ReactNode;
  htmlFor?: string;
  className?: string;
  optional?: boolean;
}

export const Label: React.FC<LabelProps> = ({ children, htmlFor, className = '', optional }) => {
  return (
    <label 
      htmlFor={htmlFor} 
      className={`block text-label font-bold text-ink-500 mb-1.5 w-full ${className}`}
    >
      {children}
      {optional && <span className="text-ink-400 font-normal ml-1">(frivillig)</span>}
    </label>
  );
};

// --- INPUTS ---

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  error?: string;
  icon?: React.ReactNode;
}

// Using forwardRef and ensuring the component is top-level prevents the focus-loss bug
export const Input = forwardRef<HTMLInputElement, InputProps>(({ className = '', error, icon, ...props }, ref) => {
  return (
    <div className="w-full">
      <div className="relative">
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none">
            {icon}
          </div>
        )}
        <input
          {...props}
          ref={ref}
          className={`
            w-full bg-white border border-line rounded-xl text-[16px] md:text-body font-bold text-ink-900 
            outline-none focus:ring-2 focus:ring-coffee-500/20 focus:border-coffee-500 transition-all 
            placeholder:text-ink-400 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed
            py-3.5 px-4 ${icon ? 'pl-10' : ''}
            ${error ? 'border-tone-rust/40 ring-2 ring-tone-rust-soft' : ''}
            ${className}
          `}
        />
      </div>
      {error && <p className="text-label text-tone-rust font-bold mt-1">{error}</p>}
    </div>
  );
});

Input.displayName = 'Input';

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  error?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({ className = '', error, ...props }, ref) => {
  return (
    <div className="w-full">
      <textarea
        {...props}
        ref={ref}
        className={`
          w-full bg-white border border-line rounded-xl text-[16px] md:text-body font-medium text-ink-900 
          outline-none focus:ring-2 focus:ring-coffee-500/20 focus:border-coffee-500 transition-all 
          placeholder:text-ink-400 shadow-sm resize-none py-3 px-4 min-h-[100px]
          ${error ? 'border-tone-rust/40 ring-2 ring-tone-rust-soft' : ''}
          ${className}
        `}
      />
      {error && <p className="text-label text-tone-rust font-bold mt-1">{error}</p>}
    </div>
  );
});

TextArea.displayName = 'TextArea';

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  error?: string;
  label?: string; // Optional embedded label style
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(({ className = '', error, children, ...props }, ref) => {
  return (
    <div className="w-full relative">
      <select
        {...props}
        ref={ref}
        className={`
          w-full bg-white border border-line rounded-xl text-[16px] md:text-body font-bold text-ink-700 
          outline-none focus:ring-2 focus:ring-coffee-500/20 focus:border-coffee-500 transition-all 
          shadow-sm appearance-none py-3.5 px-4 pr-10
          ${error ? 'border-tone-rust/40 ring-2 ring-tone-rust-soft' : ''}
          ${className}
        `}
      >
        {children}
      </select>
      {/* Custom Chevron */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-ink-500">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
      </div>
      {error && <p className="text-label text-tone-rust font-bold mt-1">{error}</p>}
    </div>
  );
});

Select.displayName = 'Select';

// --- BUTTONS ---

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'danger' | 'ghost';
  isLoading?: boolean;
  icon?: React.ReactNode;
  /** Sätt till false för fristående verktygsfälts-/headerknappar som inte ska fylla sin container. */
  fullWidth?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  className = '',
  isLoading,
  icon,
  disabled,
  fullWidth = true,
  ...props
}) => {

  const baseStyles = `${fullWidth ? 'w-full' : 'w-auto px-5'} py-4 rounded-xl font-bold text-meta transition-all flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100`;
  
  const variants = {
    primary: "bg-coffee-900 text-white hover:bg-coffee-800 shadow-lg shadow-coffee-900/20 border border-transparent",
    secondary: "bg-paper-100 text-ink-700 hover:bg-paper-200 hover:text-ink-900 border border-transparent",
    outline: "bg-transparent text-ink-500 border-2 border-line hover:border-coffee-400 hover:text-coffee-600",
    danger: "bg-tone-rust-soft text-tone-rust hover:bg-tone-rust-soft/70 border border-tone-rust/15",
    ghost: "bg-transparent text-ink-500 hover:text-coffee-600 hover:bg-paper-50 border-transparent"
  };

  return (
    <button
      {...props}
      disabled={disabled || isLoading}
      className={`${baseStyles} ${variants[variant]} ${className}`}
    >
      {isLoading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
};
